import React, { useEffect, useMemo, useState } from "react";
import "./css/search.css";
import { useNavigate, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft } from "@fortawesome/free-solid-svg-icons";

export default function Search() {
    const navigate = useNavigate();
    const location = useLocation();

    // 검색어 / 태그 / 결과 카운트 (데모용)
    const [keyword, setKeyword] = useState("");
    const [popular, setPopular] = useState(["탐정법인홍익 모바일 신분증", "사설탐정사 2급", "사설탐정사 전문가"]);
    const [isFilterOpen, setFilterOpen] = useState(false);

    // 신청된 자격증 목록 확인 함수
    const getPendingCertificates = () => {
        const pendingCerts = new Set();
        const approvedCerts = new Set();
        
        // 탐정법인홍익 모바일 신분증 확인
        const pendingCert = localStorage.getItem("hb_pending_cert");
        if (pendingCert) {
            try {
                const data = JSON.parse(pendingCert);
                if (data.name) {
                    if (data.status === "pending") {
                        pendingCerts.add(data.name);
                    } else if (data.status === "approved") {
                        approvedCerts.add(data.name);
                    }
                }
            } catch (e) {
                console.error("Failed to parse pending cert:", e);
            }
        }
        
        // 다른 자격증들 확인
        const pendingCertsList = localStorage.getItem("hb_pending_certs");
        if (pendingCertsList) {
            try {
                const data = JSON.parse(pendingCertsList);
                if (Array.isArray(data)) {
                    data.forEach((cert) => {
                        if (cert.name) {
                            if (cert.status === "pending") {
                                pendingCerts.add(cert.name);
                            } else if (cert.status === "approved") {
                                approvedCerts.add(cert.name);
                            }
                        }
                    });
                }
            } catch (e) {
                console.error("Failed to parse pending certs:", e);
            }
        }
        
        return { pending: pendingCerts, approved: approvedCerts };
    };

    // 탐정법인홍익 모바일 신분증 가맹점 가입 여부
    const [mobileJoined, setMobileJoined] = useState(false);
    // 일반 자격증(기타) 가입 여부
    const [generalJoined, setGeneralJoined] = useState(false);

    const refreshMobileJoinStatus = () => {
        // 탐정법인홍익 모바일 신분증 또는 일반 자격증 중 하나라도 가입되어 있으면
        // 모든 자격증에 대해 가입 완료로 처리
        if (localStorage.getItem("hb_mobile_id_joined") === "true") return true;
        if (localStorage.getItem("hb_general_joined") === "true") return true;
        
        // 가입 정보가 있으면 가입된 것으로 간주
        if (localStorage.getItem("hb_mobile_join_info")) return true;
        if (localStorage.getItem("hb_general_join_info")) return true;

        // 기존 신청 데이터가 있으면 가입된 것으로 간주
        const pendingCert = localStorage.getItem("hb_pending_cert");
        if (pendingCert) {
            try {
                const data = JSON.parse(pendingCert);
                if (data.name === "탐정법인홍익 모바일 신분증") {
                    return true;
                }
            } catch (e) {
                console.error("Failed to parse pending cert:", e);
            }
        }

        return false;
    };

    const refreshGeneralJoinStatus = () => {
        // 탐정법인홍익 모바일 신분증 또는 일반 자격증 중 하나라도 가입되어 있으면
        // 모든 자격증에 대해 가입 완료로 처리
        if (localStorage.getItem("hb_mobile_id_joined") === "true") return true;
        if (localStorage.getItem("hb_general_joined") === "true") return true;
        
        // 가입 정보가 있으면 가입된 것으로 간주
        if (localStorage.getItem("hb_mobile_join_info")) return true;
        if (localStorage.getItem("hb_general_join_info")) return true;
        
        return false;
    };

    useEffect(() => {
        setMobileJoined(refreshMobileJoinStatus());
        setGeneralJoined(refreshGeneralJoinStatus());
    }, [location]);

    // ---- 모달 상태(필터) ----
    const [activeField, setActiveField] = useState(["전체"]); // 분야 선택 (태그) - 기본값: 전체
    const [orgName, setOrgName] = useState("");
    const [scale, setScale] = useState("all");
    const [sinceYear, setSinceYear] = useState(2000);
    const [sort, setSort] = useState("popular");

    // 필터 적용 상태 (실제 적용되는 필터)
    const [appliedFilters, setAppliedFilters] = useState({
        activeField: ["전체"],
        orgName: "",
        scale: "all",
        sinceYear: 2000,
        sort: "popular",
    });

    // 안내 모달 상태
    const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
    const [selectedCert, setSelectedCert] = useState(null);
    const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false);

    // 전체 카드 데이터 (탐정법인홍익 / 국가공인탐정협회 컨셉)
    const allCards = useMemo(
        () => [
            { hot: true, name: "탐정법인홍익 모바일 신분증", org: "탐정법인홍익", field: "신분/인증", apply: 184, year: 2024 },
            { hot: true, name: "사설탐정사 2급", org: "국가공인탐정협회", field: "탐정/자격", apply: 202, year: 2023 },
            { hot: true, name: "사설탐정사 전문가", org: "탐정법인홍익", field: "탐정/교육", apply: 156, year: 2024 },
            { hot: false, name: "디지털포렌식 전문가", org: "홍익평생교육원", field: "기술/보안", apply: 128, year: 2022 },
            { hot: false, name: "민간조사사", org: "국가공인탐정협회", field: "탐정/자격", apply: 98, year: 2023 },
            { hot: false, name: "탐정 실무과정", org: "홍익평생교육원", field: "탐정/교육", apply: 87, year: 2021 },
            { hot: false, name: "사이버 수사 전문가", org: "탐정법인홍익", field: "기술/보안", apply: 76, year: 2024 },
            { hot: false, name: "신원조사 전문가", org: "국가공인탐정협회", field: "탐정/자격", apply: 65, year: 2022 },
        ],
        []
    );

    // 필터링된 카드
    const cards = useMemo(() => {
        let filtered = [...allCards];

        // 1. 검색어 필터 (검색어는 실시간 적용)
        if (keyword.trim()) {
            const searchLower = keyword.toLowerCase();
            filtered = filtered.filter(
                (c) =>
                    c.name.toLowerCase().includes(searchLower) ||
                    c.org.toLowerCase().includes(searchLower) ||
                    c.field.toLowerCase().includes(searchLower)
            );
        }

        // 2. 분야 필터 (적용된 필터 사용)
        if (appliedFilters.activeField.length > 0 && !appliedFilters.activeField.includes("전체")) {
            filtered = filtered.filter((c) =>
                appliedFilters.activeField.some((field) => {
                    // 필터의 분야와 카드의 분야를 비교 (슬래시 포함)
                    return c.field === field || c.field.includes(field) || field.includes(c.field);
                })
            );
        }

        // 3. 운영 기관 필터
        if (appliedFilters.orgName.trim()) {
            const orgLower = appliedFilters.orgName.toLowerCase();
            filtered = filtered.filter((c) => c.org.toLowerCase().includes(orgLower));
        }

        // 4. 운영 규모 필터
        if (appliedFilters.scale !== "all") {
            filtered = filtered.filter((c) => c.apply >= parseInt(appliedFilters.scale));
        }

        // 5. 등록연도 필터
        filtered = filtered.filter((c) => c.year >= appliedFilters.sinceYear);

        // 6. 정렬
        if (appliedFilters.sort === "popular") {
            filtered.sort((a, b) => b.apply - a.apply);
        } else if (appliedFilters.sort === "name") {
            filtered.sort((a, b) => a.name.localeCompare(b.name, "ko"));
        } else if (appliedFilters.sort === "new") {
            filtered.sort((a, b) => b.year - a.year);
        }

        // 7. 탐정법인홍익 모바일 신분증을 맨 위로
        const mobileIdIndex = filtered.findIndex((c) => c.name === "탐정법인홍익 모바일 신분증");
        if (mobileIdIndex > 0) {
            const mobileIdCard = filtered.splice(mobileIdIndex, 1)[0];
            filtered.unshift(mobileIdCard);
        }

        return filtered;
    }, [allCards, keyword, appliedFilters]);

    // 태그 토글(상단 인기 태그)
    const onClickPopular = (t) => setKeyword(t);

    // 필터 모달 토글
    const openFilter = () => setFilterOpen(true);
    const closeFilter = () => setFilterOpen(false);

    // 분야 태그 토글(모달 내부)
    const toggleField = (t) =>
        setActiveField((prev) => (prev.includes(t) ? prev.filter((v) => v !== t) : [...prev, t]));

    const resetFilter = () => {
        setActiveField(["전체"]);
        setOrgName("");
        setScale("all");
        setSinceYear(2000);
        setSort("popular");
        // 초기화 시 필터도 즉시 적용
        setAppliedFilters({
            activeField: ["전체"],
            orgName: "",
            scale: "all",
            sinceYear: 2000,
            sort: "popular",
        });
    };

    // 필터 적용 버튼
    const applyFilter = () => {
        setAppliedFilters({
            activeField: activeField.length === 0 ? ["전체"] : activeField,
            orgName: orgName,
            scale: scale,
            sinceYear: sinceYear,
            sort: sort,
        });
        setFilterOpen(false);
    };

    return (
        <div className="container-wrap search-wrap">
            {/* ===== Header ===== */}
            {/* <header className="setting-header">
                <img
                    src="/img/hongik_logo.png"
                    alt="탐정법인홍익"
                    className="setting-logo"
                    onClick={() => navigate("/")}
                />
            </header> */}
            <header className="search-header">
                <div className="header-top">
                    <div className="logo">
                        <img
                            src="/img/hongik_logo.png"
                            alt="탐정법인홍익"
                            // onClick={() => navigate("/")}
                            style={{ width: "110px" }}
                        />
                    </div>
                    <div className="search-bar">
                        <input
                            type="text"
                            placeholder="자격증명, 기관명, 분야로 검색"
                            value={keyword}
                            onChange={(e) => setKeyword(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    e.preventDefault();
                                    // 검색어는 이미 keyword state로 관리되므로 필터링이 자동으로 적용됨
                                }
                            }}
                        />
                        <button
                            type="button"
                            onClick={() => {
                                // 검색어는 이미 keyword state로 관리되므로 필터링이 자동으로 적용됨
                                // 필요시 추가 검색 로직 구현 가능
                            }}
                        >
                            검색
                        </button>
                    </div>
                    <button className="filter-btn" onClick={openFilter}>필터</button>
                    <div
                        style={{
                            position: "absolute",
                            left: "5px",
                            top: "-5px",
                            padding: "8px",
                            cursor: "pointer",
                            zIndex: 10
                        }}
                        onClick={() => {
                            // main 페이지의 + 슬라이드(인덱스 0)로 이동
                            navigate("/main", {
                                state: { slideIndex: 0 }
                            });
                        }}
                    >
                        <FontAwesomeIcon icon={faChevronLeft} style={{ fontSize: "20px", color: "#333" }} />
                    </div>
                </div>

                <div className="popular-tags">
                    {popular.map((t) => (
                        <button key={t} className="tag" type="button" onClick={() => onClickPopular(t)}>
                            {t}
                        </button>
                    ))}
                </div>
            </header>

            <div className="summary">&nbsp;&nbsp;&nbsp;총 {allCards.length}건 중 {cards.length}건의 자격증 표시 중</div>

            {/* ===== Card List ===== */}
            <div className="card-list">
                {cards.length === 0 ? (
                    <div style={{
                        textAlign: "center",
                        padding: "60px 20px",
                        color: "#999",
                        fontSize: "16px"
                    }}>
                        검색 결과가 없습니다.
                    </div>
                ) : (
                    cards.map((c, idx) => {
                        const certStatus = getPendingCertificates();
                        const isPending = certStatus.pending.has(c.name);
                        const isApproved = certStatus.approved.has(c.name);
                        const isMobileId = c.name === "탐정법인홍익 모바일 신분증";

                        return (
                            <div key={`${c.name}-${idx}`} className="card">
                                {c.hot && <div className="badge">HOT</div>}
                                <h3>{c.name}</h3>
                                <p>운영기관: {c.org}</p>
                                <p>분야: {c.field}</p>
                                <p>연간 응시자 수: {c.apply}명</p>
                                {isPending ? (
                                    <a
                                        href="#"
                                        className="btn-detail"
                                        style={{
                                            opacity: 0.6,
                                            cursor: "not-allowed",
                                            pointerEvents: "none"
                                        }}
                                        onClick={(e) => {
                                            e.preventDefault();
                                        }}
                                    >
                                        대기중
                                    </a>
                                ) : isApproved ? (
                                    <a
                                        href="#"
                                        className="btn-detail"
                                        style={{
                                            backgroundColor: "#999",
                                            color: "#fff",
                                            cursor: "not-allowed",
                                            pointerEvents: "none"
                                        }}
                                        onClick={(e) => {
                                            e.preventDefault();
                                        }}
                                    >
                                        신청완료
                                    </a>
                                ) : (
                                    <a
                                        href="#"
                                        className="btn-detail"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            setSelectedCert(c);
                                            setIsInfoModalOpen(true);
                                        }}
                                    >
                                        상세보기
                                    </a>
                                )}
                            </div>
                        );
                    })
                )}
            </div>

            {/* ===== Filter Modal ===== */}
            {isFilterOpen && (
                <div className="filter-overlay active" onClick={(e) => e.target === e.currentTarget && closeFilter()}>
                    <div className="filter-modal" role="dialog" aria-modal="true">
                        <h2>필터 설정</h2>

                        <div className="filter-section">
                            <label>분야 선택</label>
                            <div className="tag-list">
                                {["전체", "신분/인증", "탐정/자격", "탐정/교육", "기술/보안"].map((t) => (
                                    <span
                                        key={t}
                                        className={`tag ${activeField.includes(t) ? "active" : ""}`}
                                        onClick={() => {
                                            if (t === "전체") {
                                                // 전체 선택 시 다른 선택 해제
                                                setActiveField(["전체"]);
                                            } else {
                                                // 다른 항목 선택 시 전체 해제
                                                const newFields = activeField.filter(f => f !== "전체");
                                                if (newFields.includes(t)) {
                                                    // 이미 선택된 항목이면 제거
                                                    if (newFields.length === 1) {
                                                        setActiveField(["전체"]);
                                                    } else {
                                                        setActiveField(newFields.filter(f => f !== t));
                                                    }
                                                } else {
                                                    // 선택되지 않은 항목이면 추가
                                                    setActiveField([...newFields, t]);
                                                }
                                            }
                                        }}
                                    >
                                        {t}
                                    </span>
                                ))}
                            </div>
                        </div>

                        <div className="filter-section">
                            <label>운영 기관</label>
                            <input
                                type="text"
                                placeholder="기관명을 입력하세요"
                                value={orgName}
                                onChange={(e) => setOrgName(e.target.value)}
                            />
                        </div>

                        <div className="filter-section">
                            <label>운영 규모</label>
                            <div className="toggle-group">
                                <label><input type="radio" name="scale" checked={scale === "all"} onChange={() => setScale("all")} /> 전체</label>
                                <label><input type="radio" name="scale" checked={scale === "150"} onChange={() => setScale("150")} /> 연 150명 이상</label>
                                {/* <label><input type="radio" name="scale" checked={scale === "500"} onChange={() => setScale("500")} /> 연 500명 이상</label> */}
                            </div>
                        </div>

                        <div className="filter-section">
                            <label>등록연도</label>
                            <input
                                type="range"
                                min="2000"
                                max="2025"
                                value={sinceYear}
                                className="year-slider"
                                onChange={(e) => setSinceYear(Number(e.target.value))}
                            />
                            <div className="range-value">{sinceYear}년 이후</div>
                        </div>

                        <div className="filter-section">
                            <label>정렬 기준</label>
                            <div className="toggle-group">
                                <label><input type="radio" name="sort" checked={sort === "popular"} onChange={() => setSort("popular")} /> 인기순</label>
                                <label><input type="radio" name="sort" checked={sort === "name"} onChange={() => setSort("name")} /> 이름순</label>
                                <label><input type="radio" name="sort" checked={sort === "new"} onChange={() => setSort("new")} /> 최신등순</label>
                            </div>
                        </div>

                        <div className="filter-actions">
                            <button className="btn-reset" onClick={resetFilter}>초기화</button>
                            <button className="btn-apply" onClick={applyFilter}>필터 적용</button>
                        </div>
                    </div>
                </div>
            )}

            {/* ===== 안내 모달 ===== */}
            {isInfoModalOpen && selectedCert && (
                <div className="info-modal-overlay" onClick={() => setIsInfoModalOpen(false)}>
                    <div className="info-modal" onClick={(e) => e.stopPropagation()}>
                        {/* 헤더 */}
                        <div className="info-modal-header">
                            <div className="info-modal-logo-container">
                                {selectedCert.name === "탐정법인홍익 모바일 신분증" ? (
                                    <img src="/img/hongik_logo.png" alt="탐정법인홍익" style={{ width: "90%", margin: '0 auto' }} />
                                ) : (
                                    <div style={{ textAlign: "left", width: "100%" }}>
                                        <h2 style={{ margin: 0, fontSize: "20px", fontWeight: "bold" }}>{selectedCert.name}</h2>
                                        <p style={{ margin: "10px 0 0 0", fontSize: "14px", color: "#666" }}>{selectedCert.org}</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* 본문 */}
                        <div className="info-modal-body">
                            {/* 기관 설명 섹션 */}
                            <div className="info-modal-content">
                                {selectedCert.name === "탐정법인홍익 모바일 신분증" ? (
                                    <>
                                        <p>탐정법인홍익은 국가공인탐정협회와 함께</p>
                                        <p>탐정·조사·포렌식 분야의 전문 인력을 양성하고</p>
                                        <p>공인된 모바일 신분증으로 소속을 인증합니다.</p>
                                    </>
                                ) : (
                                    <>
                                        <p>{selectedCert.org}에서 발급하는</p>
                                        <p>{selectedCert.name} 자격증입니다.</p>
                                        <p>분야: {selectedCert.field}</p>
                                    </>
                                )}
                            </div>

                            {/* 신청 안내 섹션 */}
                            <div className="info-modal-apply-section">
                                <div className="info-modal-link">
                                    {selectedCert.name === "탐정법인홍익 모바일 신분증" ? (
                                        mobileJoined ? (
                                            <>
                                                {/* 발급신청서 제출한 후<br /> */}
                                                신청하기를 통해 탐정법인홍익 소속을 인증받으세요
                                            </>
                                        ) : (
                                            <>최초 1회는 가입신청서를 작성해야 합니다.</>
                                        )
                                    ) : generalJoined ? (
                                        <>
                                            {/* 발급신청서 제출한 후<br /> */}
                                            신청하기을 통해 자격증을 발급받으세요
                                        </>
                                    ) : (
                                        <>최초 1회는 가입신청서를 작성해야 합니다.</>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* 버튼 */}
                        <div className="info-modal-footer">
                            <button
                                className="info-modal-btn"
                                onClick={() => {
                                    if (!selectedCert) return;

                                    const isMobileIdSelected = selectedCert.name === "탐정법인홍익 모바일 신분증";

                                    // 1) 탐정법인홍익 모바일 신분증 - 미가입 상태 → 가입하기(발급신청 페이지 이동)
                                    if (isMobileIdSelected && !mobileJoined) {
                                        setIsInfoModalOpen(false);
                                        navigate("/issuance", {
                                            state: { cert: selectedCert, mode: "join", redirectTo: "/search" },
                                        });
                                        return;
                                    }

                                    // 2) 탐정법인홍익 모바일 신분증 - 가입 완료 상태 → 바로 신청 완료 모달(대기 상태)
                                    if (isMobileIdSelected && mobileJoined) {
                                        const applicationData = {
                                            name: "탐정법인홍익 모바일 신분증",
                                            number: selectedCert.number || "",
                                            issuer: selectedCert.org || "탐정법인홍익",
                                            status: "pending",
                                            appliedAt: new Date().toISOString(),
                                        };
                                        try {
                                            localStorage.setItem("hb_pending_cert", JSON.stringify(applicationData));
                                            localStorage.setItem("hb_mobile_id_joined", "true");
                                        } catch (err) {
                                            console.error("Failed to store certificate:", err);
                                        }

                                        setMobileJoined(true);
                                        setIsInfoModalOpen(false);
                                        setIsSubmitModalOpen(true);
                                        return;
                                    }

                                    // 3) 기타 자격증 - 미가입 상태 → 가입하기(발급신청 페이지 이동)
                                    if (!generalJoined) {
                                        setIsInfoModalOpen(false);
                                        navigate("/issuance", {
                                            state: { cert: selectedCert, mode: "join", redirectTo: "/search" },
                                        });
                                        return;
                                    }

                                    // 4) 기타 자격증 - 가입 완료 상태 → 바로 신청 완료 모달(대기 상태)
                                    const applicationData = {
                                        name: selectedCert.name,
                                        number: selectedCert.number || "",
                                        issuer: selectedCert.org || "",
                                        status: "pending",
                                        appliedAt: new Date().toISOString(),
                                    };
                                    try {
                                        const existingCerts = JSON.parse(localStorage.getItem("hb_pending_certs") || "[]");
                                        existingCerts.push(applicationData);
                                        localStorage.setItem("hb_pending_certs", JSON.stringify(existingCerts));
                                    } catch (err) {
                                        console.error("Failed to store pending cert:", err);
                                    }

                                    setIsInfoModalOpen(false);
                                    setIsSubmitModalOpen(true);
                                }}
                            >
                                {selectedCert?.name === "탐정법인홍익 모바일 신분증"
                                    ? mobileJoined
                                        ? "신청하기"
                                        : "가입하기"
                                    : generalJoined
                                        ? "신청하기"
                                        : "가입하기"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* 즉시 신청 완료 모달 */}
            {isSubmitModalOpen && (
                <div
                    className="info-modal-overlay"
                    onClick={() => setIsSubmitModalOpen(false)}
                >
                    <div
                        className="info-modal"
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            padding: "32px 28px",
                            textAlign: "center",
                            borderRadius: "16px",
                            boxShadow: "0 18px 50px rgba(0,0,0,0.18)",
                            maxWidth: "360px",
                            width: "90%",
                        }}
                    >
                        <div
                            style={{
                                width: 70,
                                height: 70,
                                margin: "0 auto 16px",
                                borderRadius: "50%",
                                background: "linear-gradient(135deg, #ff8a80, #ff6a63)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                boxShadow: "0 10px 25px rgba(255,106,99,0.35)",
                            }}
                        >
                            <svg
                                width="34"
                                height="34"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                aria-hidden="true"
                            >
                                <path
                                    d="M9.5 16.2L5.8 12.5C5.41 12.11 4.78 12.11 4.39 12.5C4 12.89 4 13.52 4.39 13.91L8.79 18.31C9.18 18.7 9.81 18.7 10.2 18.31L19.61 8.9C20 8.51 20 7.88 19.61 7.49C19.22 7.1 18.59 7.1 18.2 7.49L9.5 16.2Z"
                                    fill="white"
                                />
                            </svg>
                        </div>
                        <h3 style={{ margin: "0 0 8px", fontSize: "20px", fontWeight: 700, color: "#111" }}>
                            자격증 신청이 완료되었습니다.
                        </h3>
                        <p style={{ margin: "0 0 18px", color: "#555", lineHeight: 1.5 }}>
                            신청이 정상적으로 접수되었어요.<br />
                            관리자 승인 후 확인가능합니다..
                        </p>
                        <button
                            className="info-modal-btn"
                            style={{
                                width: "100%",
                                padding: "12px 0",
                                borderRadius: "10px",
                                background: "#ff6a63",
                                color: "#fff",
                                fontWeight: 700,
                                border: "none",
                                boxShadow: "0 10px 20px rgba(255,106,99,0.3)",
                            }}
                            onClick={() => setIsSubmitModalOpen(false)}
                        >
                            확인
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
